import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Header, Segment, Dropdown, DropdownProps } from 'semantic-ui-react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import agent from '../app/api/agent';

const MepzDashboard: React.FC = () => {
  const [categoryWise, setCategoryWise] = useState([]);
  const [departmentWise, setDepartmentWise] = useState<any[]>([]);
  const [lotWise, setLotWise] = useState<any[]>([]);
  const [registered, setRegistered] = useState<any[]>([]);
  
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => 2014 + i).map(year => ({
    key: year,
    text: year.toString(),
    value: year,
  }));

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const fetchData = useCallback(async (year: number) => {
    try {
      const [categoryRes, departmentRes, lotRes, registeredRes] = await Promise.all([
        agent.Activities.mepzChartCategory(year),
        agent.Activities.mepzCharts('Department'),
        agent.Activities.mepzCharts('Lot No'),
        agent.Activities.mepzchartRegistered(),
      ]);

      setCategoryWise(categoryRes.data);
      setDepartmentWise(departmentRes.data);
      setLotWise(lotRes.data);
      setRegistered(registeredRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedYear);
  }, [selectedYear, fetchData]);

  const onYearChange = (e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    setSelectedYear(data.value as number);
  };

  const generateRandomColors = (count: number, alpha = 1) => {
    return Array.from({ length: count }, () => {
      const randomColor = Math.floor(Math.random() * 16777215).toString(16);
      const hexColor = `#${randomColor.padStart(6, '0')}`;
      const [r, g, b] = [parseInt(hexColor.slice(1, 3), 16), parseInt(hexColor.slice(3, 5), 16), parseInt(hexColor.slice(5, 7), 16)];
      return alpha < 1 ? `rgba(${r}, ${g}, ${b}, ${alpha})` : hexColor;
    });
  };

  const categoryColors = useMemo(() => generateRandomColors(categoryWise.length), [categoryWise]);
  const departmentColors = useMemo(() => generateRandomColors(departmentWise[0]?.categoryChartsList.length || 0), [departmentWise]);
  const lotColors = useMemo(() => generateRandomColors(lotWise[0]?.categoryChartsList.length || 0, 0.2), [lotWise]);

  const completeData = Array.from({ length: 11 }, (_, i) => {
    const year = 2014 + i;
    const found = registered.find((item: any) => item.year === year);
    return { year, count: found ? found.count : 0 };
  });

  const histogramData = {
    labels: yearOptions.map(option => option.value.toString()),
    datasets: [{
      label: "Total Files Registered",
      backgroundColor: 'rgba(75, 192, 192, 0.6)',
      data: completeData.map(yearData => yearData.count),
    }],
  };

  const doughnutData = {
    labels: categoryWise.map((cat: any) => cat.name),
    datasets: [{
      label: 'Files Registered',
      backgroundColor: categoryColors,
      data: categoryWise.map((cat: any) => cat.count),
    }],
  };

  const barData = {
    labels: yearOptions.map(option => option.value.toString()),
    datasets: departmentWise[0]?.categoryChartsList.map((category: any, index: number) => ({
      label: category.name,
      backgroundColor: departmentColors[index],
      data: yearOptions.map(option => {
        const yearData = departmentWise.find(dept => dept.year === option.value);
        return yearData?.categoryChartsList.find((cat: any) => cat.name === category.name)?.count || 0;
      }),
    })) || [],
  };

  const lineData = {
    labels: yearOptions.map(option => option.value.toString()),
    datasets: lotWise[0]?.categoryChartsList.map((category: any, index: number) => ({
      label: category.name,
      borderColor: lotColors[index].replace(/rgba?\(([^)]+)\)/, 'rgba($1, 1)'),
      backgroundColor: lotColors[index],
      data: yearOptions.map(option => {
        const yearData = lotWise.find(dept => dept.year === option.value);
        return yearData?.categoryChartsList.find((cat: any) => cat.name === category.name)?.count || 0;
      }),
      fill: true,
      borderWidth: 2,
    })) || [],
  };

  const chartOptions = {
    maintainAspectRatio: false,
  };

  return (
    <>
      <Header as='h2' style={{ marginLeft: "20px", marginTop: "20px" }}>Dashboard</Header>
      <Segment style={{ display: 'flex', margin: "20px" }}>
        <Segment style={{ flex: 1 }}>
          <Segment style={{ display: 'flex', flexDirection: 'row', gap: '20px', width: "100%" }}>
            <div style={{ flex: 1 }}>
              <Header as='h4' style={{ margin: "0px", lineHeight: "3em"}}>Total Files Registered</Header>
              <Segment style={{ height: '300px' }}>
                <Bar data={histogramData} options={chartOptions} />
              </Segment>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex',alignItems: 'center',justifyContent: 'space-between'}}>
                <Header as='h4' style={{ margin: "0px"}}>Files Registered by Category</Header>
                <div style={{display: 'flex',alignItems: 'baseline'}}>
                <label style={{fontWeight:"bold", marginRight:"5px"}}>Year</label>
                <Dropdown
                  placeholder='Select Year'
                fluid
                selection
                options={yearOptions}
                onChange={onYearChange}
                style={{ width: '200px', marginBottom: '10px' }}
                value={selectedYear}
              />
              </div>
              </div>
              <Segment style={{ height: '300px' }}>
                {categoryWise.length > 0 ? <Doughnut data={doughnutData} options={chartOptions} /> : <Header as='h4' textAlign='center'>No data to display</Header>}
              </Segment>
            </div>
          </Segment>
          <Segment style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              <Header as='h4'>Files Registered by Department</Header>
              <Segment style={{ height: '300px' }}>
                <Bar data={barData} options={chartOptions} />
              </Segment>
            </div>
            <div style={{ flex: 1 }}>
              <Header as='h4'>Files Registered by Lot.No</Header>
              <Segment style={{ height: '300px' }}>
                <Line data={lineData} options={chartOptions} />
              </Segment>
            </div>
          </Segment>
        </Segment>
      </Segment>
    </>
  );
};

export default MepzDashboard;
